import { useState } from "react"
import { AlertCircle, CheckCircle2, ChevronDown, ChevronUp, Grid3x3, Plus } from "lucide-react"
import { Button } from "@/components/ui/button"
import type { CoverageMatrix } from "@/stores/strategy-store"

interface WinThemeCoverageBarProps {
  themeCount: number
  coverage: CoverageMatrix | null
  onViewMatrix: () => void
  onAddThemeForCriterion?: ((criterionId: string) => void) | undefined
}

const CHIP_LIMIT = 3

export function WinThemeCoverageBar({
  themeCount,
  coverage,
  onViewMatrix,
  onAddThemeForCriterion,
}: WinThemeCoverageBarProps) {
  const [expanded, setExpanded] = useState(false)

  const percent = coverage?.coveragePercent ?? 0
  const isFullCoverage = percent === 100 && (coverage?.totalCriteria ?? 0) > 0

  const gaps = coverage?.rows.filter(r => r.themeIds.length === 0) ?? []
  const hasGaps = gaps.length > 0
  const shouldCollapse = gaps.length > CHIP_LIMIT + 1
  const visibleGaps = shouldCollapse && !expanded ? gaps.slice(0, CHIP_LIMIT) : gaps
  const hiddenCount = gaps.length - visibleGaps.length

  return (
    <div className="border border-border rounded-xl bg-card shadow-sm p-4">
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium">{themeCount} theme{themeCount !== 1 ? 's' : ''} defined</span>
          {coverage && coverage.totalCriteria > 0 && (
            <>
              <span className="text-meta">&middot;</span>
              <span className="text-meta">
                Criteria coverage: {coverage.coveredCriteria} of {coverage.totalCriteria}
              </span>
            </>
          )}
        </div>
        {coverage && coverage.totalCriteria > 0 && (
          <Button variant="ghost" size="sm" className="text-xs text-[hsl(var(--brand))] hover:text-[hsl(var(--brand))]" onClick={onViewMatrix}>
            <Grid3x3 className="h-3 w-3 mr-1" />
            View Matrix
          </Button>
        )}
      </div>

      {coverage && coverage.totalCriteria > 0 && (
        <>
          <div className="flex items-center gap-1">
            {(() => {
              const maxSegments = 20
              const segments = Math.min(coverage.totalCriteria, maxSegments)
              const filledSegments = Math.round((coverage.coveredCriteria / coverage.totalCriteria) * segments)
              return Array.from({ length: segments }).map((_, i) => (
                <div
                  key={i}
                  className={`h-2 flex-1 rounded-sm transition-colors duration-300 ${
                    i < filledSegments
                      ? isFullCoverage
                        ? 'bg-[hsl(var(--status-success))]'
                        : 'bg-primary'
                      : 'bg-muted'
                  }`}
                />
              ))
            })()}
          </div>

          {isFullCoverage && (
            <div className="flex items-center gap-1.5 mt-2">
              <CheckCircle2 className="h-3.5 w-3.5 text-[hsl(var(--status-success))]" />
              <span className="text-xs font-medium text-[hsl(var(--status-success))]">All evaluation criteria addressed</span>
            </div>
          )}

          {!isFullCoverage && hasGaps && (
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <span className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground">
                <AlertCircle className="h-3 w-3 text-[hsl(var(--destructive))]" />
                {gaps.length} not addressed
              </span>
              {visibleGaps.map(gap => {
                const isClickable = onAddThemeForCriterion != null
                const Tag = isClickable ? 'button' : 'div'
                return (
                  <Tag
                    key={gap.criterionId}
                    type={isClickable ? 'button' : undefined}
                    onClick={isClickable ? () => onAddThemeForCriterion(gap.criterionId) : undefined}
                    className={`inline-flex items-center gap-1.5 h-[26px] px-2.5 rounded-md border border-[hsl(var(--destructive)/0.18)] bg-[hsl(var(--destructive)/0.06)] text-xs font-medium text-[hsl(var(--destructive))] transition-colors ${
                      isClickable ? 'hover:bg-[hsl(var(--destructive)/0.10)] hover:border-[hsl(var(--destructive)/0.3)] cursor-pointer' : ''
                    }`}
                    title={isClickable ? `Add a theme to address "${gap.criterionTitle}"` : gap.criterionTitle}
                  >
                    {isClickable && <Plus className="h-3 w-3 opacity-80" />}
                    <span>{gap.criterionTitle}</span>
                  </Tag>
                )
              })}
              {shouldCollapse && (
                <button
                  type="button"
                  onClick={() => setExpanded(v => !v)}
                  className="inline-flex items-center gap-1 h-[26px] px-2.5 rounded-md text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                >
                  {expanded ? (
                    <>
                      Show less
                      <ChevronUp className="h-3 w-3" />
                    </>
                  ) : (
                    <>
                      +{hiddenCount} more
                      <ChevronDown className="h-3 w-3" />
                    </>
                  )}
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}
